import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import {
    LogOut,
    Download,
    Search,
    Loader2,
    CheckCircle2,
    Circle,
    ChevronDown,
    Mail,
    Phone,
    MapPin,
    Stethoscope,
    NotebookPen,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { SEO } from "../components/medical/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
    const { user, ready, logout } = useAuth();
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all"); // all | pending | contacted
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        if (!ready || !user) return;
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/enquiries`);
                setEnquiries(data);
            } catch (err) {
                toast.error("Failed to load enquiries.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [ready, user]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return enquiries.filter((e) => {
            if (filter === "pending" && e.contacted) return false;
            if (filter === "contacted" && !e.contacted) return false;
            if (!q) return true;
            return [e.name, e.email, e.country, e.treatment, e.phone]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q));
        });
    }, [enquiries, query, filter]);

    const stats = useMemo(() => {
        const total = enquiries.length;
        const contacted = enquiries.filter((e) => e.contacted).length;
        return { total, contacted, pending: total - contacted };
    }, [enquiries]);

    if (ready && !user) return <Navigate to="/admin/login" replace />;
    if (!ready)
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-brand-700" />
            </div>
        );

    const update = async (id, patch) => {
        try {
            const { data } = await axios.patch(`${API}/enquiries/${id}`, patch);
            setEnquiries((prev) => prev.map((e) => (e.id === id ? data : e)));
            toast.success("Updated.");
        } catch {
            toast.error("Update failed.");
        }
    };

    const downloadCsv = async () => {
        try {
            const { data } = await axios.get(`${API}/enquiries/export.csv`, {
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `truheal-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("CSV export failed.");
        }
    };

    return (
        <div className="min-h-screen bg-stone-50">
            <SEO title="Admin Dashboard" description="TruHeal India — manage patient enquiries." />
            {/* Header */}
            <header className="bg-white border-b border-stone-200 sticky top-0 z-20">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src="https://res.cloudinary.com/deq3rnrha/image/upload/v1777362730/57F66F69-6741-41CE-9E1B-06B8AB12B13D_izmfua.png"
                            alt="TruHeal India"
                            className="h-10 w-auto"
                        />
                        <span className="font-display font-semibold text-stone-900 hidden sm:inline">
                            Admin
                        </span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <div className="text-sm text-stone-600 hidden sm:block">
                            {user?.email}
                        </div>
                        <Button
                            onClick={logout}
                            variant="outline"
                            data-testid="admin-logout"
                            className="rounded-full border-stone-300"
                        >
                            <LogOut className="h-4 w-4 mr-2" />
                            Log out
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-5 sm:px-8 py-8 md:py-12">
                {/* Title + stats */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
                    <div>
                        <h1 className="font-display text-3xl md:text-4xl font-bold text-stone-900">
                            Patient Enquiries
                        </h1>
                        <p className="text-stone-500 mt-1 text-sm">
                            Every form submission is recorded here.
                        </p>
                    </div>
                    <div className="flex gap-3">
                        {[
                            { label: "Total", v: stats.total, testid: "stat-total" },
                            { label: "Pending", v: stats.pending, testid: "stat-pending" },
                            { label: "Contacted", v: stats.contacted, testid: "stat-contacted" },
                        ].map((s) => (
                            <div
                                key={s.label}
                                className="px-4 py-3 rounded-2xl bg-white border border-stone-200 min-w-[100px]"
                                data-testid={s.testid}
                            >
                                <div className="text-xs uppercase tracking-wider text-stone-500">
                                    {s.label}
                                </div>
                                <div className="font-display text-2xl font-bold text-stone-900">
                                    {s.v}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Toolbar */}
                <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
                    <div className="flex-1 relative">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                        <Input
                            data-testid="admin-search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by name, email, country, treatment..."
                            className="h-11 rounded-xl pl-9 bg-white"
                        />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {["all", "pending", "contacted"].map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                data-testid={`filter-${f}`}
                                className={`px-4 h-11 rounded-full text-sm font-medium border transition-colors ${
                                    filter === f
                                        ? "bg-brand-700 border-brand-700 text-white"
                                        : "bg-white border-stone-200 text-stone-700 hover:bg-stone-50"
                                }`}
                            >
                                {f.charAt(0).toUpperCase() + f.slice(1)}
                            </button>
                        ))}
                        <Button
                            onClick={downloadCsv}
                            data-testid="admin-export-csv"
                            className="rounded-full bg-orange-500 hover:bg-orange-600 text-white h-11"
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Export CSV
                        </Button>
                    </div>
                </div>

                {/* Enquiry list */}
                {loading ? (
                    <div className="py-20 flex items-center justify-center text-stone-500">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        Loading enquiries...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-20 text-center text-stone-500 rounded-3xl bg-white border border-stone-200">
                        No enquiries found.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filtered.map((e) => {
                            const open = expanded === e.id;
                            const created = e.created_at
                                ? new Date(e.created_at).toLocaleString()
                                : "";
                            return (
                                <article
                                    key={e.id}
                                    data-testid={`enquiry-row-${e.id}`}
                                    className="rounded-2xl bg-white border border-stone-200 overflow-hidden"
                                >
                                    <button
                                        onClick={() => setExpanded(open ? null : e.id)}
                                        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-stone-50 transition-colors"
                                    >
                                        <div
                                            className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                e.contacted
                                                    ? "bg-emerald-50 text-emerald-600"
                                                    : "bg-orange-50 text-orange-600"
                                            }`}
                                        >
                                            {e.contacted ? (
                                                <CheckCircle2 className="h-5 w-5" />
                                            ) : (
                                                <Circle className="h-5 w-5" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-display font-semibold text-stone-900 truncate">
                                                    {e.name}
                                                </span>
                                                <span className="text-xs text-stone-500">
                                                    · {e.country}
                                                </span>
                                            </div>
                                            <div className="mt-0.5 text-xs text-stone-500 truncate">
                                                {e.treatment} · {created}
                                            </div>
                                        </div>
                                        <ChevronDown
                                            className={`h-4 w-4 text-stone-400 flex-shrink-0 transition-transform ${
                                                open ? "rotate-180" : ""
                                            }`}
                                        />
                                    </button>
                                    {open && (
                                        <div className="border-t border-stone-100 p-5 grid md:grid-cols-2 gap-6 bg-stone-50/40">
                                            <div className="space-y-3 text-sm">
                                                <div className="flex items-center gap-2 text-stone-700">
                                                    <Mail className="h-4 w-4 text-brand-700" />
                                                    <a
                                                        href={`mailto:${e.email}`}
                                                        className="hover:underline"
                                                    >
                                                        {e.email}
                                                    </a>
                                                </div>
                                                <div className="flex items-center gap-2 text-stone-700">
                                                    <Phone className="h-4 w-4 text-brand-700" />
                                                    <a
                                                        href={`https://wa.me/${(e.phone || "").replace(/[^0-9]/g, "")}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="hover:underline"
                                                    >
                                                        {e.phone}
                                                    </a>
                                                </div>
                                                <div className="flex items-center gap-2 text-stone-700">
                                                    <MapPin className="h-4 w-4 text-brand-700" />
                                                    {e.country}
                                                </div>
                                                <div className="flex items-center gap-2 text-stone-700">
                                                    <Stethoscope className="h-4 w-4 text-brand-700" />
                                                    {e.treatment}
                                                </div>
                                                {e.message && (
                                                    <div className="mt-3 p-3 rounded-xl bg-white border border-stone-200 text-stone-700 whitespace-pre-wrap text-sm">
                                                        {e.message}
                                                    </div>
                                                )}
                                                <div className="pt-2 flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        onClick={() =>
                                                            update(e.id, { contacted: !e.contacted })
                                                        }
                                                        data-testid={`toggle-contacted-${e.id}`}
                                                        className={`rounded-full ${
                                                            e.contacted
                                                                ? "bg-stone-100 hover:bg-stone-200 text-stone-800"
                                                                : "bg-emerald-600 hover:bg-emerald-700 text-white"
                                                        }`}
                                                    >
                                                        {e.contacted ? "Mark as Pending" : "Mark as Contacted"}
                                                    </Button>
                                                    <a
                                                        href={`mailto:${e.email}?subject=Your%20TruHeal%20India%20enquiry`}
                                                        className="inline-flex items-center px-4 h-9 rounded-full border border-stone-300 text-sm font-medium text-stone-700 hover:bg-white"
                                                    >
                                                        Reply via Email
                                                    </a>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-xs uppercase tracking-wider text-stone-500 font-semibold flex items-center gap-1">
                                                    <NotebookPen className="h-3.5 w-3.5" /> Internal notes
                                                </label>
                                                <NotesEditor enquiry={e} onSave={update} />
                                            </div>
                                        </div>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
};

const NotesEditor = ({ enquiry, onSave }) => {
    const [val, setVal] = useState(enquiry.notes || "");
    const [saving, setSaving] = useState(false);
    const dirty = val !== (enquiry.notes || "");

    const save = async () => {
        setSaving(true);
        await onSave(enquiry.id, { notes: val });
        setSaving(false);
    };

    return (
        <>
            <Textarea
                data-testid={`notes-${enquiry.id}`}
                value={val}
                onChange={(e) => setVal(e.target.value)}
                placeholder="Add internal notes about this enquiry..."
                rows={5}
                className="mt-2 rounded-xl"
            />
            <div className="mt-2 flex justify-end">
                <Button
                    size="sm"
                    disabled={!dirty || saving}
                    onClick={save}
                    data-testid={`save-notes-${enquiry.id}`}
                    className="rounded-full bg-brand-700 hover:bg-brand-800 text-white"
                >
                    {saving ? "Saving..." : "Save notes"}
                </Button>
            </div>
        </>
    );
};
